import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { contactsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Search, Pencil, Trash2, Mail, Phone, Building2, X, Download, UserPlus, ChevronRight } from 'lucide-react';
import { getInitials, getAvatarColor, truncate } from '../lib/utils';
import { toast } from 'sonner';

function LoyaltyBadge({ loyalty }) {
  const map = {
    VIP: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
    Gold: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    Standard: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${map[loyalty] || map.Standard}`}>
      {loyalty}
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    Active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    Pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    Inactive: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${map[status] || map.Inactive}`}>
      {status}
    </span>
  );
}

export default function ContactsPage() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterLoyalty, setFilterLoyalty] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [position, setPosition] = useState('');
  const [notes, setNotes] = useState('');
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [saving, setSaving] = useState(false);

  const loadContacts = useCallback(async () => {
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterLoyalty !== 'all') params.loyalty = filterLoyalty;
      const response = await contactsAPI.listWithStats(params);
      setContacts(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des contacts');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filterStatus, filterLoyalty]);

  useEffect(() => { loadContacts(); }, [loadContacts]);

  const resetForm = () => {
    setFirstName(''); setLastName(''); setEmail('');
    setPhone(''); setCompany(''); setPosition('');
    setNotes(''); setTags([]); setNewTag('');
  };

  const openCreateDialog = (e) => {
    e.stopPropagation();
    setEditingContact(null);
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (e, contact) => {
    e.stopPropagation();
    setEditingContact(contact);
    setFirstName(contact.first_name);
    setLastName(contact.last_name);
    setEmail(contact.email);
    setPhone(contact.phone || '');
    setCompany(contact.company || '');
    setPosition(contact.position || '');
    setNotes(contact.notes || '');
    setTags(contact.tags || []);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formData = { first_name: firstName, last_name: lastName, email, phone, company, position, notes, tags };
      if (editingContact) {
        await contactsAPI.update(editingContact.id, formData);
        toast.success('Contact mis à jour');
      } else {
        await contactsAPI.create(formData);
        toast.success('Contact créé');
      }
      setDialogOpen(false);
      loadContacts();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Supprimer ce contact ?')) return;
    try {
      await contactsAPI.delete(id);
      toast.success('Contact supprimé');
      loadContacts();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const addTag = () => {
    if (newTag && !tags.includes(newTag)) {
      setTags([...tags, newTag]);
      setNewTag('');
    }
  };

  // Footer stats
  const avgLtv = contacts.length > 0
    ? (contacts.reduce((s, c) => s + (c.ltv || 0), 0) / contacts.length)
    : 0;
  const activeCount = contacts.filter(c => c.account_status === 'Active').length;
  const vipCount = contacts.filter(c => c.loyalty_status === 'VIP').length;

  if (loading) {
    return (
      <div className="space-y-5" data-testid="contacts-loading">
        <div className="flex items-center justify-between">
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-10 w-44" />
        </div>
        <Skeleton className="h-11 w-full max-w-md" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fade-in" data-testid="contacts-page">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Customer Database</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Gérez et suivez vos {contacts.length} contacts actifs
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-2">
            <Download size={15} />
            Export
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreateDialog} size="sm" className="gap-2" data-testid="add-contact-btn">
                <UserPlus size={15} />
                Nouveau contact
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingContact ? 'Modifier le contact' : 'Nouveau contact'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">Prénom *</Label>
                    <Input id="first_name" value={firstName} onChange={e => setFirstName(e.target.value)} required data-testid="contact-first-name" />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Nom *</Label>
                    <Input id="last_name" value={lastName} onChange={e => setLastName(e.target.value)} required data-testid="contact-last-name" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} required data-testid="contact-email" />
                </div>
                <div>
                  <Label htmlFor="phone">Téléphone</Label>
                  <Input id="phone" value={phone} onChange={e => setPhone(e.target.value)} data-testid="contact-phone" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="company">Entreprise</Label>
                    <Input id="company" value={company} onChange={e => setCompany(e.target.value)} data-testid="contact-company" />
                  </div>
                  <div>
                    <Label htmlFor="position">Poste</Label>
                    <Input id="position" value={position} onChange={e => setPosition(e.target.value)} data-testid="contact-position" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea id="notes" value={notes} onChange={e => setNotes(e.target.value)} rows={3} data-testid="contact-notes" />
                </div>
                <div>
                  <Label>Tags</Label>
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={newTag}
                      onChange={e => setNewTag(e.target.value)}
                      placeholder="Ajouter un tag"
                      onKeyPress={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                    />
                    <Button type="button" variant="outline" onClick={addTag}>Ajouter</Button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {tags.map(tag => (
                      <Badge key={tag} variant="secondary" className="flex items-center gap-1 text-xs">
                        {tag}
                        <X className="w-3 h-3 cursor-pointer" onClick={() => setTags(tags.filter(t => t !== tag))} />
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                  <Button type="submit" disabled={saving} data-testid="save-contact-btn">
                    {saving ? 'Enregistrement...' : (editingContact ? 'Mettre à jour' : 'Créer')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search & filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Rechercher par nom, email, entreprise..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="contacts-search"
          />
        </div>
        <div className="flex items-center gap-2">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-36 h-9">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterLoyalty} onValueChange={setFilterLoyalty}>
            <SelectTrigger className="w-36 h-9">
              <SelectValue placeholder="Fidélité" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toute fidélité</SelectItem>
              <SelectItem value="vip">VIP</SelectItem>
              <SelectItem value="gold">Gold</SelectItem>
              <SelectItem value="standard">Standard</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="contacts-table">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40">Contact</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40 hidden md:table-cell">Entreprise</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40 hidden lg:table-cell">LTV</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40 hidden lg:table-cell">Fidélité</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40 hidden xl:table-cell">Statut</th>
                <th className="text-right px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {contacts.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-muted-foreground">
                    Aucun contact trouvé
                  </td>
                </tr>
              ) : (
                contacts.map(contact => (
                  <tr
                    key={contact.id}
                    className="hover:bg-muted/30 transition-colors cursor-pointer"
                    onClick={() => navigate(`/contacts/${contact.id}`)}
                    data-testid={`contact-row-${contact.id}`}
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9 flex-shrink-0">
                          <AvatarFallback className={`text-xs font-semibold ${getAvatarColor(`${contact.first_name} ${contact.last_name}`)}`}>
                            {getInitials(`${contact.first_name} ${contact.last_name}`)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-semibold text-foreground">{contact.first_name} {contact.last_name}</p>
                          {contact.position && <p className="text-xs text-muted-foreground">{contact.position}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell">
                      {contact.company && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Building2 size={14} />
                          <span>{contact.company}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-4 hidden lg:table-cell">
                      <span className="font-semibold text-foreground">
                        {(contact.ltv || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
                      </span>
                    </td>
                    <td className="px-5 py-4 hidden lg:table-cell">
                      <LoyaltyBadge loyalty={contact.loyalty_status || 'Standard'} />
                    </td>
                    <td className="px-5 py-4 hidden xl:table-cell">
                      <StatusBadge status={contact.account_status || 'Inactive'} />
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={(e) => openEditDialog(e, contact)}
                          className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                          data-testid={`edit-contact-${contact.id}`}
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          onClick={(e) => handleDelete(e, contact.id)}
                          className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                          data-testid={`delete-contact-${contact.id}`}
                        >
                          <Trash2 size={15} />
                        </button>
                        <ChevronRight size={15} className="text-muted-foreground/50" />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Table footer */}
        {contacts.length > 0 && (
          <div className="px-5 py-3 border-t border-border bg-muted/20 flex flex-wrap gap-4 items-center">
            <p className="text-xs text-muted-foreground">
              {contacts.length} contact{contacts.length > 1 ? 's' : ''}
            </p>
            <p className="text-xs text-muted-foreground">
              LTV moyen : <span className="font-semibold text-foreground">
                {avgLtv.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
              </span>
            </p>
            <p className="text-xs text-muted-foreground">
              Actifs : <span className="font-semibold text-emerald-600">{activeCount}</span>
            </p>
            <p className="text-xs text-muted-foreground">
              VIP : <span className="font-semibold text-violet-600">{vipCount}</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
